import { useState, useRef, useEffect } from "react";
import Label from "./Label";

const InputDropdown = ({
  label,
  name,
  id,
  value,
  onChange,
  placeholder,
  required = false,
  className = '',
  labelClass = '',
  options = [],
  'data-testid': dataTestId,
}) => {
  const testId = dataTestId ?? (name ? `${name}-input` : 'input');
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const handleSelect = (selectedValue) => {
    onChange({ target: { name, value: selectedValue } });
    setIsOpen(false);
  };

  // Close dropdown if clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="relative flex flex-col gap-1" ref={dropdownRef}>
      {label && (
        <Label htmlFor={name} data-testid={name ? `${name}-label` : undefined} className={labelClass}>
          {label}
        </Label>
      )}

      {/* Input box with arrow */}
      <div className="relative w-full">
        <input
          data-testid={testId}
          type="text"
          name={name}
          id={id}
          value={value}
          onChange={onChange}
          onFocus={() => setIsOpen(true)}
          placeholder={placeholder}
          required={required}
          className={`${className} pr-10`}
          autoComplete="off"
        />

        {/* Arrow Icon */}
        <div
          data-testid={`${testId}-toggle`}
          className="absolute inset-y-0 right-2 flex items-center cursor-pointer"
          onClick={() => setIsOpen((prev) => !prev)}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              d="M19 9L12 15L5 9"
              stroke="black"
              strokeOpacity="0.87"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="dark:stroke-white"
            />
          </svg>
        </div>
      </div>

      {/* Dropdown list */}
      {isOpen && options.length > 0 && (
        <div className="dropdownMenuHolder absolute top-full right-0 shadow-appShadow block w-full rounded-md z-50 dark:shadow-darkThemeDropdownMenuShadow">
          <ul data-testid={`${testId}-options`} className=" mt-1 z-10 w-full bg-white dark:bg-darkThemeDropdownBg dark:border-0 border border-gray-300 rounded-md shadow-md max-h-32 overflow-y-auto">
            {options?.map((option, index) => {
              const isSelected = option === value;
              return (
                <li
                  key={index}
                  data-testid={`${testId}-option-${index}`}
                  onMouseDown={() => handleSelect(option)}
                  className={`px-3 py-2.5 text-dark-grey dark:text-white text-xs text-left leading-4 lg:text-sm lg:leading-5 font-normal tracking-[-0.408px] border-b border-grey dark:border-darkThemeBorder last:border-0 flex justify-between items-center gap-2 cursor-pointer 
                    ${isSelected ? 'bg-[#FFF1C6] dark:bg-darkThemeDropdownItemBg' : 'bg-white dark:bg-darkThemeDropdownBg'
                    }`}
                >
                  <span>{option}</span>
                  {isSelected && (
                    <span>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                      >
                        <path
                          d="M8.49215 15C8.24102 15 8.00244 14.8995 7.82665 14.7238L4.27311 11.1702C3.90896 10.8061 3.90896 10.2033 4.27311 9.8392C4.63725 9.47506 5.23997 9.47506 5.60412 9.8392L8.49215 12.7272L14.9463 6.27311C15.3104 5.90896 15.9132 5.90896 16.2773 6.27311C16.6414 6.63725 16.6414 7.23997 16.2773 7.60412L9.15766 14.7238C8.98186 14.8995 8.74329 15 8.49215 15Z"
                          fill="#FF8C65"
                        />
                      </svg>
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

export default InputDropdown;
